import { useRef, useState } from "react";
import { X } from "lucide-react";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import type { FilterField } from "@/config/filterFields";
import { describeFieldFilter, describeFilterValues, isNegativeMode, isTextMode, modeWord, sameFieldFilter, type FieldFilter } from "@/lib/filter-model";
import { cn } from "@/lib/utils";
import { ValueEditor } from "./ValueEditor";

/**
 * One field's filter in the bar: its body opens the editor, × removes it. A
 * chip without a filter is an empty dashed slot ("+ Brand"). Edits stay in a
 * draft and reach the report once, when the editor closes - Done, Enter or a
 * click elsewhere; Esc throws the draft away. One reload per edit instead of
 * one per checkbox.
 */
export function FilterChip({ field, filter, onApply, defaultOpen = false, onDismiss }: {
  field: FilterField;
  /** null: an empty slot. */
  filter: FieldFilter | null;
  onApply: (next: FieldFilter | null) => void;
  defaultOpen?: boolean;
  /** Closed without a filter (a field just picked from "+ Filter"). */
  onDismiss?: () => void;
}) {
  const empty: FieldFilter = { mode: "include", values: [] };
  const [open, setOpen] = useState(defaultOpen);
  const [draft, setDraft] = useState<FieldFilter>(filter ?? empty);
  const cancelled = useRef(false);
  // The chip (or slot) the editor hangs on, and whether it closed by a click
  // elsewhere - then focus stays where that click went.
  const anchor = useRef<HTMLElement>(null);
  const outside = useRef(false);

  const openEditor = () => {
    setDraft(filter ?? empty);
    cancelled.current = false;
    outside.current = false;
    setOpen(true);
  };
  // `override`: a value's "only" applies at once, before the draft state lands.
  const close = (apply: boolean, override?: FieldFilter) => {
    setOpen(false);
    const d = override ?? draft;
    const next = d.values.length ? d : null;
    if (apply && !sameFieldFilter(next, filter)) onApply(next);
    if (!filter && !(apply && next)) onDismiss?.();
  };

  const exclude = filter ? isNegativeMode(filter.mode) : false;
  return (
    <Popover open={open} onOpenChange={(o) => (o ? openEditor() : close(!cancelled.current))}>
      <PopoverAnchor asChild>
        {filter ? (
          <span
            ref={anchor}
            className={cn(
              "inline-flex max-w-[18rem] items-center overflow-hidden rounded-full border text-xs",
              exclude
                ? "border-destructive/30 bg-destructive/10 dark:border-red-400/40 dark:bg-red-400/10"
                : "border-primary/30 bg-primary/10",
            )}
          >
            <button
              type="button"
              data-chip-key={field.key}
              onClick={() => (open ? close(true) : openEditor())}
              title={`${field.label} ${modeWord(filter.mode)} ${filter.values.map((v) => (isTextMode(filter.mode) ? `“${v}”` : (field.valueLabel?.(v) ?? v))).join(", ")}`}
              aria-label={`${describeFieldFilter(field.label, filter, field.valueLabel)}. Edit`}
              aria-expanded={open}
              className="min-w-0 cursor-pointer truncate py-1 pr-1 pl-2.5 text-left"
            >
              <span className={cn("font-medium", exclude && "text-destructive dark:text-red-400")}>{field.label}</span>{" "}
              <span className="text-muted-foreground">{modeWord(filter.mode)}</span>{" "}
              {describeFilterValues(filter, 2, field.valueLabel)}
            </button>
            <button
              type="button"
              onClick={() => onApply(null)}
              aria-label={`Remove ${field.label} filter`}
              className="cursor-pointer self-stretch px-2 text-muted-foreground hover:text-foreground"
            >
              <X className="size-3" aria-hidden />
            </button>
          </span>
        ) : (
          <button
            ref={anchor as React.RefObject<HTMLButtonElement>}
            type="button"
            data-chip-key={field.key}
            onClick={() => (open ? close(true) : openEditor())}
            aria-expanded={open}
            className="inline-flex cursor-pointer items-center rounded-full border border-dashed border-accent-foreground/50 px-2.5 py-1 text-xs text-accent-foreground hover:bg-accent"
          >
            + {field.label}
          </button>
        )}
      </PopoverAnchor>
      <PopoverContent
        align="start"
        // rem, not px: at a 24px root font 20rem stays inside a phone.
        className="w-[min(20rem,calc(100vw-1rem))] p-3"
        onEscapeKeyDown={() => {
          cancelled.current = true;
        }}
        // A press on the chip itself is not "outside": its own click toggles
        // the editor. Applying on that pointerdown and reopening on the click
        // put the old values back (review 30.10.2026).
        onInteractOutside={(e) => {
          if (anchor.current?.contains(e.target as Node)) e.preventDefault();
          else outside.current = true;
        }}
        // Radix returns focus only to a PopoverTrigger; the chip is an anchor,
        // so focus fell to <body>.
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          if (!outside.current) (anchor.current?.querySelector("button") ?? anchor.current)?.focus();
        }}
      >
        <ValueEditor field={field} draft={draft} onDraft={setDraft} onDone={(next) => close(true, next)} />
      </PopoverContent>
    </Popover>
  );
}
